import { PropsWithChildren, useCallback, useMemo } from 'react';
import { useCollapse } from 'react-collapsed';
import { useMount } from 'react-use';
import cn from 'classnames';
import { useShallow } from 'zustand/react/shallow';
import { MdKeyboardArrowDown, MdOutlinePersonAdd } from 'react-icons/md';
import { Button, useDisclosure } from '@ds';
import { ServiceRulesViolations } from '@lp/features/Scheduler/types';
import { getDailyAndWeeklyServiceViolations } from '@lp/features/Scheduler/validation.utils';
import { ScheduleViolationTooltip } from '@lp/features/Scheduler/components';
import { SOrganGroup } from '@lp/features/Scheduler/SchedulerSurgeons/types';
import { InsertSurgeonToRegionModal } from '@lp/features/modals/InsertSurgeonToRegionModal/InsertSurgeonToRegionModal';
import { sSchedulerSelectors, useSSchedulerStore } from '@lp/core/store/scheduler';
import { Region, SResource, SResourcesWithShifts } from '@/api/apis/resources/schedule';

type RegionWrapperProps = PropsWithChildren<{
  region: Region;
  readonly?: boolean;
  userList?: SResource[];
  organGroup: SOrganGroup;
  shiftsData: SResourcesWithShifts;
  regionsDictionary: Record<string, Region>;
  initiallyExpanded?: boolean;
  violations?: ServiceRulesViolations;
}>;

export const RegionWrapper = ({
  region,
  readonly,
  userList = [],
  organGroup,
  shiftsData,
  regionsDictionary,
  initiallyExpanded = false,
  violations,
  children,
}: RegionWrapperProps) => {
  const { isOpen: isAssignSModalOpen, onOpen, onClose } = useDisclosure();
  const isExpanded = useSSchedulerStore(useShallow(sSchedulerSelectors.getRegionState(organGroup, region.id)));
  const setRegionExpand = useSSchedulerStore.use.setRegionExpand();
  const assignSurgeonsToRegion = useSSchedulerStore.use.assignSurgeonsToRegion();
  const isEmpty = userList.length === 0;
  const showContent = !!isExpanded && !isEmpty;
  const canAssign = !readonly && (isEmpty || isExpanded);
  const showUserCount = (readonly && isEmpty) || (!isExpanded && !canAssign);
  const defaultExpanded = initiallyExpanded && isExpanded === undefined;
  const { getCollapseProps, getToggleProps } = useCollapse({ isExpanded: showContent, defaultExpanded });

  const violationList = useMemo(() => getDailyAndWeeklyServiceViolations(violations), [violations]);

  const onUserListChange = useCallback(
    (userIds: string[]) => {
      assignSurgeonsToRegion(organGroup, region.id, userIds);
      onClose();
    },
    [onClose, assignSurgeonsToRegion, organGroup, region.id],
  );

  useMount(() => {
    if (initiallyExpanded && isExpanded === undefined) setRegionExpand(organGroup, region.id, true);
  });

  return (
    <div className="flex flex-col bg-white rounded-large shadow-small">
      <div className="flex justify-between items-center">
        <button
          {...getToggleProps({ onClick: () => setRegionExpand(organGroup, region.id) })}
          className={cn('flex items-center gap-2 p-3', { 'opacity-60': isEmpty })}
          disabled={isEmpty}
        >
          <MdKeyboardArrowDown size={24} className={cn('text-[#111] transition', { '-rotate-90': !showContent })} />
          <span className="text-xl-sb text-[#111]">{region.name}</span>
          {!isExpanded && violationList.length > 0 && (
            <ScheduleViolationTooltip items={violationList} withLabel>
              <div className="px-1.5 h-4 rounded-lg bg-danger-30 text-white text-s-sb">{violationList.length}</div>
            </ScheduleViolationTooltip>
          )}
        </button>
        {showUserCount && <span className="text-s-r text-base-20 pe-4">{userList.length} surgeons</span>}
        {canAssign && (
          <Button disableRipple size="sm" variant="light" startContent={<MdOutlinePersonAdd />} onClick={onOpen}>
            Assign Surgeon to Region
          </Button>
        )}
        {isAssignSModalOpen && (
          <InsertSurgeonToRegionModal
            region={region}
            organGroup={organGroup}
            userList={userList}
            shiftsData={shiftsData}
            regionsDictionary={regionsDictionary}
            onClose={onClose}
            onSave={onUserListChange}
          />
        )}
      </div>
      <div {...getCollapseProps()}>{isExpanded && <div className="pt-3 pl-4">{children}</div>}</div>
    </div>
  );
};
